import React from "react";
import Icon from "components/AppIcon";
import Image from "components/AppImage";
import StatusIndicatorBadge from "components/ui/StatusIndicatorBadge";

const BiodataTable = ({
  data,
  activeTab,
  onEdit,
  onAmend,
  onViewDocuments,
  onViewDetails,
  onPrint,
  onDownload,
  onResubmit,
  onDelete,
  isDeleting = false,
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getStatusBadge = (item) => {
    switch (activeTab) {
      case "drafts":
        return <StatusIndicatorBadge status="pending" size="sm" />;
      case "submitted":
        return <StatusIndicatorBadge status="pending" size="sm" />;
      case "approved":
        return <StatusIndicatorBadge status="approved" size="sm" />;
      case "rejected":
        return <StatusIndicatorBadge status="rejected" size="sm" />;
      case "selected":
        return <StatusIndicatorBadge status="active" size="sm" />;
      case "resubmitted":
        return <StatusIndicatorBadge status="pending" size="sm" />;
      default:
        return <StatusIndicatorBadge status="default" size="sm" />;
    }
  };

  const getActionButtons = (item) => {
    switch (activeTab) {
      case "drafts":
        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit(item)}
              className="p-2 text-primary hover:bg-primary-50 rounded-md transition-colors duration-150"
              title="Edit Draft"
            >
              <Icon name="Edit" size={16} />
            </button>
            <button
              onClick={() => onViewDocuments(item)}
              className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-md transition-colors duration-150"
              title="View Documents"
            >
              <Icon name="FileText" size={16} />
            </button>
            <button
              onClick={() => onDelete && onDelete(item)}
              className="p-2 text-error hover:bg-error-50 rounded-md transition-colors duration-150 disabled:opacity-40"
              title="Delete Draft"
              disabled={isDeleting}
            >
              <Icon name="Trash2" size={16} />
            </button>
          </div>
        );
      case "submitted":
        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onViewDetails(item)}
              className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-md transition-colors duration-150"
              title="View Details"
            >
              <Icon name="Eye" size={16} />
            </button>
            <button
              onClick={() => onDownload && onDownload(item)}
              className="p-2 text-accent hover:bg-accent-50 rounded-md transition-colors duration-150"
              title="Download CV"
            >
              <Icon name="Download" size={16} />
            </button>
          </div>
        );
      case "approved":
        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onViewDetails(item)}
              className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-md transition-colors duration-150"
              title="View Details"
            >
              <Icon name="Eye" size={16} />
            </button>
            <button
              onClick={() => onDownload && onDownload(item)}
              className="p-2 text-accent hover:bg-accent-50 rounded-md transition-colors duration-150"
              title="Download CV"
            >
              <Icon name="Download" size={16} />
            </button>
            <button
              onClick={() => onPrint && onPrint(item)}
              className="p-2 text-primary hover:bg-primary-50 rounded-md transition-colors duration-150"
              title="Print CV"
            >
              <Icon name="Printer" size={16} />
            </button>
          </div>
        );
      case "rejected":
        return (
          <div className="flex items-center space-x-2">
            {(item.is_editable ||
              item.can_amend ||
              item.edit_permission_granted) && (
              <button
                onClick={() => onAmend(item)}
                className="p-2 text-warning hover:bg-warning-50 rounded-md transition-colors duration-150"
                title={
                  item.is_editable
                    ? "Edit Biodata (Permission Granted)"
                    : "Amend Biodata"
                }
              >
                <Icon name="Edit" size={16} />
              </button>
            )}
            {(item.is_editable ||
              item.can_amend ||
              item.edit_permission_granted) &&
              onResubmit && (
                <button
                  onClick={() => onResubmit(item)}
                  className="p-2 text-primary hover:bg-primary-50 rounded-md transition-colors duration-150"
                  title="Resubmit Biodata"
                >
                  <Icon name="Send" size={16} />
                </button>
              )}
            <button
              onClick={() => onViewDetails(item)}
              className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-md transition-colors duration-150"
              title="View Details"
            >
              <Icon name="Eye" size={16} />
            </button>
          </div>
        );
      case "selected":
        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onViewDetails(item)}
              className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-md transition-colors duration-150"
              title="View Details"
            >
              <Icon name="Eye" size={16} />
            </button>
            <button
              onClick={() => onDownload && onDownload(item)}
              className="p-2 text-accent hover:bg-accent-50 rounded-md transition-colors duration-150"
              title="Download CV"
            >
              <Icon name="Download" size={16} />
            </button>
          </div>
        );
      case "resubmitted":
        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onViewDetails(item)}
              className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-md transition-colors duration-150"
              title="View Details"
            >
              <Icon name="Eye" size={16} />
            </button>
            <button
              onClick={() => onDownload && onDownload(item)}
              className="p-2 text-accent hover:bg-accent-50 rounded-md transition-colors duration-150"
              title="Download CV"
            >
              <Icon name="Download" size={16} />
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  const getTableColumns = () => {
    const baseColumns = [
      { key: "id", label: "ID", width: "w-20" },
      { key: "name", label: "Name", width: "w-48" },
      { key: "passport", label: "Passport", width: "w-32" },
      { key: "type", label: "Type", width: "w-24" },
      { key: "country", label: "Country", width: "w-32" },
      { key: "medical_expiry", label: "Medical Expiry", width: "w-40" },
    ];

    switch (activeTab) {
      case "drafts":
        return [
          ...baseColumns,
          { key: "completion", label: "Progress", width: "w-32" },
          { key: "last_modified", label: "Last Modified", width: "w-36" },
          { key: "actions", label: "Actions", width: "w-32" },
        ];
      case "submitted":
        return [
          ...baseColumns,
          { key: "submitted_date", label: "Submitted", width: "w-32" },
          { key: "clab_review", label: "CLAB Review", width: "w-32" },
          { key: "actions", label: "Actions", width: "w-32" },
        ];
      case "approved":
        return [
          ...baseColumns,
          { key: "approved_date", label: "Approved", width: "w-32" },
          { key: "expires_on", label: "Expires", width: "w-32" },
          { key: "actions", label: "Actions", width: "w-32" },
        ];
      case "rejected":
        return [
          ...baseColumns,
          { key: "rejected_date", label: "Rejected", width: "w-32" },
          { key: "rejection_reason", label: "Reason", width: "w-48" },
          { key: "actions", label: "Actions", width: "w-32" },
        ];
      case "selected":
        return [
          ...baseColumns,
          { key: "selected_date", label: "Selected", width: "w-32" },
          { key: "employer", label: "Employer", width: "w-48" },
          { key: "actions", label: "Actions", width: "w-32" },
        ];
      case "resubmitted":
        return [
          ...baseColumns,
          { key: "resubmitted_date", label: "Resubmitted", width: "w-32" },
          { key: "clab_review", label: "CLAB Review", width: "w-32" },
          { key: "actions", label: "Actions", width: "w-32" },
        ];
      default:
        return [
          ...baseColumns,
          { key: "actions", label: "Actions", width: "w-32" },
        ];
    }
  };

  const renderCellContent = (item, column) => {
    switch (column.key) {
      case "id":
        return <span className="font-mono text-sm">{item.id}</span>;
      case "name":
        return (
          <div className="flex items-center space-x-3">
            <div
              className="w-8 h-8 rounded-full overflow-hidden bg-secondary-100"
              data-biodata-id={item.id}
            >
              <Image
                src={item.photo_url}
                alt={item.name}
                className="w-full h-full object-cover"
              />
            </div>
            <span className="font-medium">{item.name}</span>
          </div>
        );
      case "passport":
        return <span className="font-mono">{item.passport}</span>;
      case "type":
        return (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              item.type === "VDR"
                ? "bg-accent-100 text-accent-800"
                : "bg-primary-100 text-primary-800"
            }`}
          >
            {item.type}
          </span>
        );
      case "country":
        return <span>{item.country}</span>;
      case "medical_expiry":
        if (!item.medical_expiry_date) {
          return <span className="text-sm text-text-tertiary">Not set</span>;
        }
        const expiryDate = new Date(item.medical_expiry_date);
        const today = new Date();
        const daysUntilExpiry = Math.ceil(
          (expiryDate - today) / (1000 * 60 * 60 * 24)
        );
        return (
          <div>
            <div className="text-sm">
              {formatDate(item.medical_expiry_date)}
            </div>
            <div
              className={`text-xs ${
                daysUntilExpiry < 0
                  ? "text-error"
                  : daysUntilExpiry < 30
                  ? "text-warning"
                  : "text-success"
              }`}
            >
              {daysUntilExpiry < 0 ? "Expired" : `${daysUntilExpiry} days left`}
            </div>
          </div>
        );
      case "completion":
        return (
          <div className="flex items-center space-x-2">
            <div className="w-16 bg-secondary-200 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${item.completion}%` }}
              />
            </div>
            <span className="text-xs font-medium">{item.completion}%</span>
          </div>
        );
      case "last_modified":
      case "submitted_date":
      case "approved_date":
      case "rejected_date":
      case "selected_date":
      case "resubmitted_date":
        return <span className="text-sm">{formatDate(item[column.key])}</span>;
      case "expires_on":
        return (
          <div>
            <div className="text-sm">{formatDate(item.expires_on)}</div>
            <div
              className={`text-xs ${
                item.days_remaining < 30
                  ? "text-error"
                  : item.days_remaining < 60
                  ? "text-warning"
                  : "text-success"
              }`}
            >
              {item.days_remaining} days left
            </div>
          </div>
        );
      case "clab_review":
        return (
          <StatusIndicatorBadge
            status={item.clab_review === "In Progress" ? "pending" : "inactive"}
            size="sm"
          />
        );
      case "rejection_reason":
        return (
          <div className="max-w-xs space-y-1">
            <p
              className="text-sm text-error-700 font-semibold truncate"
              title={item.rejection_reason || "No reason provided"}
            >
              {item.rejection_reason || "No reason provided"}
            </p>
            {item.rejection_remarks && (
              <p
                className="text-xs text-text-secondary truncate"
                title={item.rejection_remarks}
              >
                Remarks: {item.rejection_remarks}
              </p>
            )}
          </div>
        );
      case "employer":
        return <span className="text-sm">{item.employer}</span>;
      case "actions":
        return getActionButtons(item);
      default:
        return item[column.key];
    }
  };

  const columns = getTableColumns();

  return (
    <div className="space-y-4">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-secondary-200">
          <thead className="bg-secondary-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider ${column.width}`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-secondary-200">
            {data.map((item) => (
              <tr
                key={item.id}
                className="hover:bg-secondary-50 transition-colors duration-150"
              >
                {columns.map((column) => (
                  <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                    {renderCellContent(item, column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BiodataTable;
